import { Link } from 'react-router-dom';
import type { SearchListing } from '@shared/api-types.js';
import { useSearchListings } from '../hooks/useSearch.js';

/**
 * Renders a horizontal scroll of active listings near a coordinate. Used on
 * city + neighbourhood landing pages to keep the page fresh on every crawl
 * (the rest of the page is mostly static SEO copy).
 *
 * Returns null when there's nothing to show — so a brand-new neighbourhood
 * page doesn't render an empty section.
 */
export function LiveListingsStrip({
  lat,
  lng,
  radiusKm = 20,
  heading,
  emptyHint,
}: {
  lat: number;
  lng: number;
  radiusKm?: number;
  heading: string;
  emptyHint?: string;
}) {
  const search = useSearchListings(
    { lat, lng, radiusKm, sort: 'nearest' } as never,
    true,
  );

  if (search.isLoading || !search.data) {
    return (
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4">{heading}</h2>
        <div className="flex gap-4 overflow-x-auto pb-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-64 card overflow-hidden animate-pulse"
            >
              <div className="aspect-[4/3] bg-navy-50" />
              <div className="p-3">
                <div className="h-3 bg-navy-50 rounded w-3/4 mb-2" />
                <div className="h-3 bg-navy-50 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  const listings = search.data.listings.slice(0, 12);
  if (listings.length === 0) {
    if (!emptyHint) return null;
    return (
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-2xl md:text-3xl font-bold text-navy mb-2">{heading}</h2>
        <p className="text-navy-600 text-sm max-w-xl">{emptyHint}</p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-end justify-between mb-5 gap-3">
        <h2 className="text-2xl md:text-3xl font-bold text-navy">{heading}</h2>
        <Link
          to={`/search?lat=${lat.toFixed(5)}&lng=${lng.toFixed(5)}&radiusKm=${radiusKm}`}
          className="text-sm font-semibold text-orange hover:underline whitespace-nowrap"
        >
          See all {search.data.listings.length} →
        </Link>
      </div>
      <div className="-mx-4 sm:mx-0 overflow-x-auto pb-2">
        <div className="flex gap-4 px-4 sm:px-0">
          {listings.map((l) => (
            <LiveCard key={l.id} listing={l} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LiveCard({ listing }: { listing: SearchListing }) {
  const photo = listing.photos?.[0];
  return (
    <Link
      to={`/listings/${listing.slug}`}
      className="group flex-shrink-0 w-64 card overflow-hidden hover:border-orange transition-colors"
    >
      <div className="aspect-[4/3] bg-navy-50 overflow-hidden">
        {photo ? (
          <img
            src={photo.thumbnailUrl || photo.url}
            alt={listing.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-3xl text-navy-300">
            🛻
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="text-sm font-semibold text-navy truncate">{listing.title}</div>
        <div className="text-xs text-navy-500 mt-0.5">
          {listing.city}
          {typeof listing.distanceKm === 'number' &&
            ` · ${listing.distanceKm.toFixed(1)} km`}
        </div>
        <div className="text-sm font-bold text-navy mt-1.5">
          ${Math.round(listing.dailyRateCents / 100)}
          <span className="text-xs font-normal text-navy-500"> / day</span>
        </div>
      </div>
    </Link>
  );
}
